import { useMemo } from 'react'
import contentTypeUtils from '../../contentTypeUtils.js'
import { normalizeEntityTruthLinks } from '../../studioWorkflowUtils.js'
import {
  apiUpdateResearchBrief,
  apiSaveIdea,
  apiReindexProofAssets,
} from '../studioApi.js'
import { useStudioState } from '../contexts/StudioStateContext'
import { useProof } from '../contexts/ProofContext'
import type {
  Idea,
  ProofAssetReindexResponse,
  ResearchBrief,
  StateSetter,
  StrategicAsset,
  StrategicAssetCoverage,
} from '../types'

const { normalizeIdea } = contentTypeUtils

export interface ResearchHandlersApi {
  updateBrief: (
    id: string,
    updates: Partial<ResearchBrief>
  ) => Promise<ResearchBrief>
  saveIdea: (idea: Idea) => Promise<Idea>
  reindexAssets: () => Promise<ProofAssetReindexResponse>
}

const DEFAULT_API: ResearchHandlersApi = Object.freeze({
  updateBrief: apiUpdateResearchBrief,
  saveIdea: apiSaveIdea,
  reindexAssets: apiReindexProofAssets,
})

export interface ResearchHandlersDeps {
  setResearchBriefs: StateSetter<ResearchBrief[]>
  setIdeas: StateSetter<Idea[]>
  setStrategicAssets: StateSetter<StrategicAsset[]>
  setStrategicAssetCoverage: StateSetter<StrategicAssetCoverage | null>
  api?: ResearchHandlersApi
}

export interface ResearchHandlers {
  handleSaveBrief: (brief: ResearchBrief) => void
  handleUpdateBrief: (
    id: string,
    updates: Partial<ResearchBrief>
  ) => Promise<ResearchBrief>
  captureResearchBrief: (brief: ResearchBrief) => Promise<Idea>
}

// Pure factory for research handlers (briefs + capture-to-idea).
//
// `captureResearchBrief` returns the normalized idea so App.jsx can drive
// the UI orchestration (tab switch + toast) outside the data layer —
// same pattern as Wave 12 Phase D's `convertAuthorityTopicCandidate`.
//
// `handleSaveBrief` triggers a proof-asset reindex as a side effect
// because saving a brief can change the strategic-asset graph; the
// reindex is fire-and-forget so a slow proof service never blocks
// the brief save.
export function createResearchHandlers(
  deps: ResearchHandlersDeps
): ResearchHandlers {
  const {
    setResearchBriefs,
    setIdeas,
    setStrategicAssets,
    setStrategicAssetCoverage,
    api = DEFAULT_API,
  } = deps

  function handleSaveBrief(brief: ResearchBrief): void {
    setResearchBriefs((prev) => [
      brief,
      ...prev.filter((item) => item.id !== brief.id),
    ])
    api
      .reindexAssets()
      .then((data) => {
        setStrategicAssets(data?.assets || [])
        setStrategicAssetCoverage(data?.coverage || null)
      })
      .catch(() => {})
  }

  async function handleUpdateBrief(
    id: string,
    updates: Partial<ResearchBrief>
  ): Promise<ResearchBrief> {
    const brief = await api.updateBrief(id, updates)
    setResearchBriefs((prev) =>
      prev.map((item) => (item.id === id ? brief : item))
    )
    return brief
  }

  async function captureResearchBrief(brief: ResearchBrief): Promise<Idea> {
    const capturedAt = new Date().toISOString()
    const truthLinks = normalizeEntityTruthLinks(brief)
    const savedIdea = await api.saveIdea({
      id: `brief-${brief.id}`,
      pillar: 'deep-dive',
      contentType: 'linkedin-post',
      text: brief.topic,
      ...truthLinks,
      metadata: {
        sourceType: 'research-note',
        evidence: [
          brief.summary,
          ...(brief.keyPoints || []).map((point) => `- ${point}`),
        ]
          .filter(Boolean)
          .join('\n'),
        sourceUrl: brief.notionBriefId ? `notion:${brief.notionBriefId}` : '',
        capturedAt,
        status: 'active',
      },
      createdAt: capturedAt,
    } as Idea)
    const normalized = normalizeIdea(savedIdea) as Idea
    setIdeas((prev) => [
      normalized,
      ...prev.filter((item) => item.id !== normalized.id),
    ])
    return normalized
  }

  return {
    handleSaveBrief,
    handleUpdateBrief,
    captureResearchBrief,
  }
}

export function useResearchHandlers(): ResearchHandlers {
  const { setResearchBriefs, setIdeas } = useStudioState()
  const { setAssets, setCoverage } = useProof()

  return useMemo(
    () =>
      createResearchHandlers({
        setResearchBriefs,
        setIdeas,
        setStrategicAssets: setAssets,
        setStrategicAssetCoverage: setCoverage,
      }),
    [setResearchBriefs, setIdeas, setAssets, setCoverage]
  )
}

export const __test__ = { DEFAULT_API }
